import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import {
  getPagos, registrarPago, getCuentaCobro, setCuentaCobro,
  textoWhatsAppCobro, generarCobrosDelDia, getCobrosProximos,
  getCapacidadDiaria, setCapacidadDiaria,
  ESTADO_PAGO_LABEL, ESTADO_PAGO_COLOR,
} from '../../lib/cobrosApi'
import {
  adminGetPedidos, adminMarcarPedidoPagado,
} from '../../lib/portalClienteApi'

export default function Cobros() {
  const [pagos, setPagos] = useState([])
  const [cuenta, setCuenta] = useState(null)
  const [capacidad, setCapacidad] = useState(20)
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('todos')  // todos | pendiente | pagado | atrasado | cancelado
  const [origen, setOrigen] = useState('todos')  // todos | suscripcion | tienda | b2b
  const [showCuenta, setShowCuenta] = useState(false)
  const [showRegistrar, setShowRegistrar] = useState(null)
  const [generando, setGenerando] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const clienteFiltradoId = searchParams.get('cliente')

  // Deep-links del dashboard: ?estado=pendiente, ?fecha=hoy
  useEffect(() => {
    const estadoParam = searchParams.get('estado')
    if (estadoParam && ['pendiente', 'pagado', 'atrasado', 'cancelado'].includes(estadoParam)) {
      setFiltro(estadoParam)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Toggle "ver pruebas" - persistido en localStorage, default OFF
  const [mostrarPruebas, setMostrarPruebas] = useState(() => {
    try { return localStorage.getItem('limpack_cobros_ver_pruebas') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('limpack_cobros_ver_pruebas', mostrarPruebas ? '1' : '0') } catch {}
  }, [mostrarPruebas])

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const [pagosSusc, c, cap, pedidos] = await Promise.all([
      getPagos(),
      getCuentaCobro(),
      getCapacidadDiaria(),
      adminGetPedidos({ limit: 100 }), // Fix 16-jul: traer TODOS los pedidos (B2C tienda + B2B). El panel los separa por tabs.
    ])
    // Mapear pedidos one-shot al formato de "pago" para mezclar en la misma tabla.
    // __origen distingue las 3 naturalezas: suscripcion (auto MP) · tienda (B2C one-shot) · b2b (post-entrega)
    const pedidosComoPago = pedidos.map(pe => ({
      id: pe.id,
      __fuente: 'pedido',
      __pedido: pe,
      __origen: pe.empresa_id ? 'b2b' : 'tienda',
      monto: pe.monto_total,
      vence: pe.fecha_corte ? pe.fecha_corte.slice(0, 10) : pe.fecha_entrega_programada,
      // 17-jul: usar pagado_at como criterio real de pago. Un pedido puede quedar en
      // 'en_armado', 'en_ruta' o 'entregado' y sigue teniendo pagado_at válido — antes
      // caían en el else → 'pendiente' y aparecían mal en el panel de Cobros aunque
      // ya estaban cobrados (bug del tick de Lylian).
      estado: pe.estado === 'cancelado' || pe.estado === 'rechazado' ? 'cancelado'
            : pe.pagado_at ? 'pagado'
            : pe.estado === 'pendiente_pago' ? 'pendiente'
            : 'pendiente',
      metodo: pe.metodo_pago,
      cliente: {
        alias: pe.empresa_id ? '🏢 B2B ' + (pe.facturacion_razon_social || pe.empresa?.razon_social || 'empresa')
             : pe.tipo === 'compra_unica' ? '📦 Kit suelto' : '🛍️ Tienda',
        barrio: '',
        titular: { nombre: pe.comprador_nombre, phone: pe.comprador_telefono },
      },
      entrega: { numero_orden: pe.numero_orden },
    }))
    // Marcar suscripciones · Los pagos con cliente_id son B2C susc, los con entrega vinculada a empresa son B2B post-entrega
    const pagosSuscMarcados = (pagosSusc || []).map(p => ({
      ...p,
      __fuente: 'suscripcion',
      __origen: p.cliente_id ? 'suscripcion' : 'b2b',
    }))
    setPagos([...pagosSuscMarcados, ...pedidosComoPago])
    setCuenta(c); setCapacidad(cap)
    setLoading(false)
  }

  async function generarHoy() {
    if (!confirm('¿Generar cobros de los clientes que tienen mensiversario hoy?')) return
    setGenerando(true)
    try {
      const r = await generarCobrosDelDia()
      alert(`✓ ${r.length} cobros generados`)
      load()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setGenerando(false) }
  }

  // Detecta si el pago/pedido está marcado como prueba
  function esPrueba(p) {
    return p.es_prueba === true
        || p.cliente?.es_prueba === true
        || p.__pedido?.es_prueba === true
  }

  const filtrados = useMemo(() => {
    let r = pagos
    if (!mostrarPruebas) r = r.filter(p => !esPrueba(p))
    if (clienteFiltradoId) {
      // Cobro de suscripción usa p.cliente_id; pedido one-shot usa __pedido.cliente_suscriptor_id
      r = r.filter(p =>
        p.cliente_id === clienteFiltradoId ||
        p.__pedido?.cliente_suscriptor_id === clienteFiltradoId
      )
    }
    if (filtro !== 'todos') r = r.filter(p => p.estado === filtro)
    if (origen !== 'todos') r = r.filter(p => p.__origen === origen)
    return r
  }, [pagos, filtro, clienteFiltradoId, mostrarPruebas, origen])

  // Contadores por origen para tabs
  const contadoresOrigen = useMemo(() => {
    const pagosReales = pagos.filter(p => !esPrueba(p))
    const c = { todos: 0, suscripcion: 0, tienda: 0, b2b: 0 }
    pagosReales.forEach(p => {
      if (p.estado !== 'pendiente' && p.estado !== 'atrasado') return
      c.todos += 1
      c[p.__origen] = (c[p.__origen] || 0) + 1
    })
    return c
  }, [pagos])

  const totalPruebas = useMemo(() => pagos.filter(esPrueba).length, [pagos])

  const clienteFiltradoNombre = clienteFiltradoId
    ? (filtrados[0]?.cliente?.titular?.nombre ||
       filtrados[0]?.cliente?.alias ||
       filtrados[0]?.__pedido?.comprador_nombre ||
       'cliente')
    : null

  function clearClienteFiltro() {
    const next = new URLSearchParams(searchParams)
    next.delete('cliente')
    setSearchParams(next)
  }

  const stats = useMemo(() => {
    // KPIs SIEMPRE excluyen es_prueba (números reales de negocio)
    const pagosReales = pagos.filter(p => !esPrueba(p))
    const counts = { todos: pagosReales.length, pendiente: 0, pagado: 0, atrasado: 0, cancelado: 0 }
    let totalPendiente = 0, totalCobrado = 0
    pagosReales.forEach(p => {
      counts[p.estado] = (counts[p.estado] || 0) + 1
      if (p.estado === 'pendiente' || p.estado === 'atrasado') totalPendiente += Number(p.monto || 0)
      if (p.estado === 'pagado') totalCobrado += Number(p.monto || 0)
    })
    return { counts, totalPendiente, totalCobrado }
  }, [pagos])

  function abrirWA(pago) {
    const tel = (pago.cliente?.titular?.phone || '').replace(/[^\d]/g, '')
    const telWa = tel ? (tel.startsWith('598') ? tel : '598' + tel.replace(/^0+/, '')) : null
    if (!telWa) { alert('El cliente no tiene teléfono cargado'); return }

    let texto
    if (pago.__fuente === 'pedido') {
      // Mensaje custom para pedidos one-shot
      const pe = pago.__pedido
      const nombre = (pe.comprador_nombre || '').split(' ')[0]
      const monto = Number(pe.monto_total).toLocaleString('es-UY')
      const esMP = (cuenta?.banco || '').toLowerCase().includes('mercadopago') ||
                   (cuenta?.banco || '').toLowerCase().includes('mercado pago')
      const cuentaTxt = cuenta?.banco
        ? (esMP
            ? `\n\n💳 *Pagá con MercadoPago:*\n• Alias/Nickname: *${cuenta.alias || cuenta.titular || 'LIMPACKUY'}*\n• Titular: ${cuenta.titular || 'LIMPACK SAS'}`
            : `\n\n📋 *Datos para transferir:*\n• Banco: ${cuenta.banco}\n• Cuenta: ${cuenta.numero || '—'}\n• Titular: ${cuenta.titular || '—'}${cuenta.alias ? `\n• Alias: ${cuenta.alias}` : ''}`)
        : ''
      texto = `Hola ${nombre}! 👋\n\nGracias por tu pedido en *Limpack* (${pe.numero_orden}).\n\n💰 Total a pagar: *$${monto}*${cuentaTxt}\n\n📲 Mandame comprobante por acá y lo confirmo.`
    } else {
      texto = textoWhatsAppCobro({ cliente: pago.cliente, pago, cuenta })
    }
    const url = `https://wa.me/${telWa}?text=${encodeURIComponent(texto)}`
    window.open(url, '_blank')
  }

  async function marcarPedidoPagado(pago) {
    if (!confirm(`¿Confirmar pago de ${pago.cliente?.titular?.nombre} · $${Number(pago.monto).toLocaleString('es-UY')}?`)) return
    try {
      await adminMarcarPedidoPagado(pago.id, null)
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  return (
    <AdminLayout>
      <div className="page">
        <h1>Cobros</h1>
        <p className="lead">Suscripciones · pagos pendientes y al día</p>

        {clienteFiltradoId && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', marginBottom: 14,
            background: '#E0F7F9', border: '1px solid #00A8B5',
            borderLeft: '4px solid #00A8B5',
            borderRadius: 8, fontSize: 13,
          }}>
            <span style={{ color: '#0E7490', fontWeight: 700 }}>
              🔍 Filtrado por cliente: <span style={{ color: '#1A3461' }}>{clienteFiltradoNombre}</span>
            </span>
            <button onClick={clearClienteFiltro}
              style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #00A8B5',
                color: '#0E7490', padding: '4px 10px', borderRadius: 6,
                fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              × Quitar filtro
            </button>
          </div>
        )}
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Cobrado</h3>
            <div className="stat" style={{ color: '#10B981' }}>${stats.totalCobrado.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
            <div className="desc">{stats.counts.pagado} pago{stats.counts.pagado !== 1 ? 's' : ''}</div>
          </div>
          <div className="card">
            <h3>Por cobrar</h3>
            <div className="stat" style={{ color: '#F59E0B' }}>${stats.totalPendiente.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
            <div className="desc">{stats.counts.pendiente + stats.counts.atrasado} pendiente{stats.counts.pendiente + stats.counts.atrasado !== 1 ? 's' : ''}</div>
          </div>
          <div className="card">
            <h3>Atrasados</h3>
            <div className="stat" style={{ color: '#EF4444' }}>{stats.counts.atrasado}</div>
            <div className="desc">3+ días sin pagar</div>
          </div>
          <div className="card" style={{ cursor: 'pointer' }} onClick={() => setShowCuenta(true)}>
            <h3>Cuenta para cobrar</h3>
            <div style={{ fontSize: 14, fontWeight: 700, color: cuenta?.banco ? '#1A3461' : '#F59E0B', marginTop: 8 }}>
              {cuenta?.banco || '⚠️ Sin configurar'}
            </div>
            <div className="desc">{cuenta?.banco ? 'Editar →' : 'Configurar →'}</div>
          </div>
        </div>

        <div className="btn-row" style={{ gap: 8, marginBottom: 8 }}>
          <button className="btn primary" onClick={generarHoy} disabled={generando}>
            {generando ? '⏳ Generando…' : '🔄 Generar mensiversarios de hoy'}
          </button>
          <button className="btn ghost" onClick={() => setShowCuenta(true)}>⚙️ Configuración</button>
        </div>

        {/* Tabs por ORIGEN (Suscripciones / Tienda / B2B post-entrega) — separa los 3 mundos que antes estaban mezclados */}
        <div className="btn-row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 10, paddingBottom: 10, borderBottom: '1px dashed #E2E8F0' }}>
          {[
            { v: 'todos', l: '📋 Todos' },
            { v: 'suscripcion', l: '🔄 Suscripciones' },
            { v: 'tienda', l: '🛍 Tienda' },
            { v: 'b2b', l: '🏢 B2B post-entrega' },
          ].map(o => (
            <button
              key={o.v}
              className={`btn ${origen === o.v ? 'primary' : 'ghost'}`}
              style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 700 }}
              onClick={() => setOrigen(o.v)}
            >{o.l} {contadoresOrigen[o.v] > 0 && <span style={{ opacity: .7 }}>({contadoresOrigen[o.v]})</span>}</button>
          ))}
        </div>

        <div className="btn-row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {['todos', 'pendiente', 'atrasado', 'pagado', 'cancelado'].map(f => (
            <button
              key={f}
              className={`btn ghost ${filtro === f ? 'active' : ''}`}
              style={{ padding: '4px 10px', fontSize: 12, ...(filtro === f ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : {}) }}
              onClick={() => setFiltro(f)}
            >{f.charAt(0).toUpperCase() + f.slice(1)} ({stats.counts[f] || 0})</button>
          ))}
          {totalPruebas > 0 && (
            <button
              type="button"
              onClick={() => setMostrarPruebas(v => !v)}
              title={mostrarPruebas ? 'Ocultar cobros marcados como prueba' : 'Mostrar cobros marcados como prueba (test/desarrollo)'}
              style={{
                padding: '4px 10px', fontSize: 12, borderRadius: 999, cursor: 'pointer', fontWeight: 700,
                background: mostrarPruebas ? '#FEF3C7' : '#F1F5F9',
                color: mostrarPruebas ? '#78350F' : '#475569',
                border: `1px solid ${mostrarPruebas ? '#FCD34D' : '#CBD5E1'}`,
              }}
            >
              🧪 {mostrarPruebas ? `Ocultar pruebas (${totalPruebas})` : `Ver pruebas (${totalPruebas})`}
            </button>
          )}
          <button className="btn ghost" onClick={load} style={{ marginLeft: 'auto' }}>↺</button>
        </div>

        {loading ? (
          <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--muted)' }}>Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--muted)' }}>
            {pagos.length === 0
              ? 'No hay cobros todavía. Se generan automáticamente al confirmar entregas.'
              : 'No hay cobros con ese filtro.'}
          </div>
        ) : (
          <table className="t-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Vence</th>
                <th>Cliente</th>
                <th>Orden</th>
                <th style={{ textAlign: 'right' }}>Monto</th>
                <th>Método</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(p => {
                const cfg = ESTADO_PAGO_COLOR[p.estado] || ESTADO_PAGO_COLOR.pendiente
                const titular = p.cliente?.titular
                const fechaVence = p.vence ? new Date(p.vence + 'T12:00:00') : null
                const diasParaVencer = fechaVence ? Math.ceil((fechaVence - new Date()) / 86400000) : null
                return (
                  <tr key={p.id}>
                    <td style={{ fontSize: 12 }}>
                      {fechaVence ? fechaVence.toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                      {diasParaVencer !== null && p.estado !== 'pagado' && p.estado !== 'cancelado' && (
                        <div style={{ fontSize: 10, color: diasParaVencer < 0 ? '#991B1B' : diasParaVencer <= 3 ? '#92400E' : 'var(--muted)' }}>
                          {diasParaVencer < 0 ? `${Math.abs(diasParaVencer)} días atrasado` : diasParaVencer === 0 ? 'vence hoy' : `en ${diasParaVencer} días`}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700 }}>{titular?.nombre || '—'}</span>
                        <span style={{
                          fontSize: 9, padding: '2px 6px', borderRadius: 999,
                          background: p.__fuente === 'pedido' ? '#FEF3C7' : 'rgba(0,196,212,.12)',
                          color:      p.__fuente === 'pedido' ? '#92400E' : '#0e7490',
                          fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                        }}>
                          {p.__fuente === 'pedido' ? 'One-shot' : 'Suscripción'}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.cliente?.alias && <strong>{p.cliente.alias} · </strong>}{p.cliente?.barrio}</div>
                    </td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)' }}>{p.entrega?.numero_orden || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800 }}>${Number(p.monto).toLocaleString('es-UY')}</td>
                    <td style={{ fontSize: 11, textTransform: 'capitalize', color: 'var(--muted)' }}>{p.metodo || '—'}</td>
                    <td>
                      <span style={{ background: cfg.bg, color: cfg.text, padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        ● {ESTADO_PAGO_LABEL[p.estado]}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {(p.estado === 'pendiente' || p.estado === 'atrasado') && (
                          <>
                            <button onClick={() => abrirWA(p)} title="Mandar WA al cliente" style={{ padding: '3px 8px', fontSize: 11, background: '#25D366', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer' }}>💬</button>
                            <button
                              onClick={() => p.__fuente === 'pedido' ? marcarPedidoPagado(p) : setShowRegistrar(p)}
                              title="Marcar pagado"
                              style={{ padding: '3px 8px', fontSize: 11, background: '#10B981', color: '#fff', border: 0, borderRadius: 4, fontWeight: 700, cursor: 'pointer' }}>✓</button>
                          </>
                        )}
                        <Link to={`/admin/clientes`} title="Ver cliente" style={{ padding: '3px 8px', fontSize: 11, background: '#F1F5F9', color: '#475569', border: 0, borderRadius: 4, textDecoration: 'none' }}>👤</Link>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {showCuenta && (
          <ModalCuenta
            cuenta={cuenta}
            capacidad={capacidad}
            onClose={() => setShowCuenta(false)}
            onSaved={() => { setShowCuenta(false); load() }}
          />
        )}

        {showRegistrar && (
          <ModalRegistrar pago={showRegistrar} onClose={() => setShowRegistrar(null)} onDone={() => { setShowRegistrar(null); load() }} />
        )}
      </div>
    </AdminLayout>
  )
}

function ModalCuenta({ cuenta, capacidad, onClose, onSaved }) {
  const [f, setF] = useState({
    banco: cuenta?.banco || '',
    numero: cuenta?.numero || '',
    titular: cuenta?.titular || '',
    alias: cuenta?.alias || '',
  })
  const [cap, setCap] = useState(capacidad || 20)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      await Promise.all([setCuentaCobro(f), setCapacidadDiaria(cap)])
      onSaved()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 480 }}>
        <h2 style={{ marginTop: 0 }}>⚙️ Configuración</h2>

        <h3 className="section-head" style={{ marginTop: 0 }}>💳 Cuenta para cobrar</h3>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 0, marginBottom: 12 }}>
          Estos datos se incluyen en el WhatsApp que mandás al cliente cuando le toca pagar.
        </p>
        <Field label="Banco">
          <input type="text" value={f.banco} onChange={e => setF({...f, banco: e.target.value})} placeholder="Ej: BROU, Itaú, Santander" />
        </Field>
        <Field label="Número de cuenta">
          <input type="text" value={f.numero} onChange={e => setF({...f, numero: e.target.value})} placeholder="Caja de Ahorro / Cuenta corriente" />
        </Field>
        <Field label="Titular">
          <input type="text" value={f.titular} onChange={e => setF({...f, titular: e.target.value})} placeholder="Nombre del titular" />
        </Field>
        <Field label="Alias (opcional)">
          <input type="text" value={f.alias} onChange={e => setF({...f, alias: e.target.value})} placeholder="LIMPACK" />
        </Field>

        <h3 className="section-head" style={{ marginTop: 16 }}>🚚 Capacidad de envíos</h3>
        <Field label="Máximo de entregas por día">
          <input type="number" min="1" max="200" value={cap} onChange={e => setCap(e.target.value)} />
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            Cuántas entregas máximas podés hacer por día. Se usa para alertar cuando un día se llena.
          </div>
        </Field>

        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ModalRegistrar({ pago, onClose, onDone }) {
  const [metodo, setMetodo] = useState(pago.metodo || 'transferencia')
  const [referencia, setReferencia] = useState('')
  const [notas, setNotas] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      await registrarPago(pago.id, { metodo, referencia, notas })
      onDone()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 420 }}>
        <h3 style={{ marginTop: 0 }}>Marcar como pagado</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0 }}>
          {pago.cliente?.titular?.nombre} · ${Number(pago.monto).toLocaleString('es-UY')}
        </p>
        <Field label="Método">
          <select value={metodo} onChange={e => setMetodo(e.target.value)}>
            <option value="transferencia">Transferencia</option>
            <option value="efectivo">Efectivo</option>
            <option value="mercadopago">MercadoPago</option>
            <option value="handy">Handy (legacy)</option>
            <option value="otro">Otro</option>
          </select>
        </Field>
        <Field label="Nro de referencia">
          <input type="text" value={referencia} onChange={e => setReferencia(e.target.value)} placeholder="Nº de transferencia" />
        </Field>
        <Field label="Notas">
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="2" />
        </Field>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : '✓ Confirmar pago'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
        {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
      </b>
      {children}
    </label>
  )
}

const modalBg = { position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }
const modalCard = { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxHeight: '90vh', overflowY: 'auto' }
